import React from 'react';
import { Box, Paper, Skeleton } from '@mui/material';
import {
  Payments as PaymentsIcon,
  Inventory2 as InventoryIcon,
  LocalPharmacy as PharmacyIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { KpiData } from '../types';
import { fmtSum } from '../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface KpiBarProps {
  kpi: KpiData | null;
  kpiLoading: boolean;
}

/** Статичные мета-данные сегмента (иконка, цвет, лейбл) */
interface SegmentMeta {
  label: string;
  color: 'success' | 'info' | 'primary' | 'secondary';
  textColor: string;
  icon: React.ReactNode;
  width: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Определяем мета-данные вне компонента — не пересоздаются при каждом рендере */
const SEGMENT_META: SegmentMeta[] = [
  { label: 'Сумма', color: 'success', textColor: '#16a34a', icon: <PaymentsIcon />, width: '22%' },
  { label: 'Количество', color: 'info', textColor: '#0284c7', icon: <InventoryIcon />, width: '45%' },
  { label: 'Позиций', color: 'primary', textColor: '#2563eb', icon: <PharmacyIcon />, width: '20%' },
  { label: 'Аптек', color: 'secondary', textColor: '#7c3aed', icon: <StoreIcon />, width: '13%' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Вычисляет отображаемое значение для каждого сегмента */
function resolveSegmentValue(index: number, kpi: KpiData | null): React.ReactNode {
  if (!kpi) return '—';
  switch (index) {
    case 0:
      return fmtSum(kpi.totalSum);
    case 1:
      return kpi.totalQtyPcs > 0
        ? `${kpi.totalQty.toLocaleString('ru-RU')} уп. / ${kpi.totalQtyPcs.toLocaleString('ru-RU')} шт.`
        : `${kpi.totalQty.toLocaleString('ru-RU')} уп.`;
    case 2:
      return kpi.count;
    case 3:
      return kpi.pharmacyCount;
    default:
      return '—';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Компактная строка KPI — занимает правую половину строки с контент-табами.
 * Высота задаётся снаружи (inherit от родителя), но Paper внутри — 44px.
 * Водяные знаки-иконки сохранены: уменьшены и прижаты в правый нижний угол
 * каждого сегмента.
 */
const KpiBar: React.FC<KpiBarProps> = ({ kpi, kpiLoading }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        display: 'flex',
        height: 44,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'white',
        width: '100%',
      }}
    >
      {SEGMENT_META.map((meta, i) => (
        <Box
          key={meta.label}
          sx={{
            flex: `0 0 ${meta.width}`,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            overflow: 'hidden',
            borderRight: i < SEGMENT_META.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            minWidth: 0,
          }}
        >
          {/* Водяной знак */}
          <Box sx={{
            position: 'absolute',
            right: -6,
            bottom: -8,
            color: theme.palette[meta.color].main,
            opacity: 0.08,
            transform: 'rotate(-15deg)',
            zIndex: 0,
            pointerEvents: 'none',
            '& svg': { fontSize: '44px' },
          }}>
            {meta.icon}
          </Box>

          {/* Лейбл */}
          <Box component="span" sx={{
            fontSize: 11,
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.3,
            zIndex: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {meta.label}
          </Box>

          {/* Значение / скелетон */}
          {kpiLoading
            ? <Skeleton width={50} height={16} sx={{ zIndex: 1 }} />
            : (
              <Box component="span" sx={{
                fontSize: 14,
                fontWeight: 800,
                color: meta.textColor,
                zIndex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {resolveSegmentValue(i, kpi)}
              </Box>
            )}
        </Box>
      ))}
    </Paper>
  );
};

export default KpiBar;
